import { Controller } from "@hotwired/stimulus"
import { successDialog, errorDialog, confirmDialog } from '../helpers/swal_helper'

export default class extends Controller {
  static targets = ["positionDisplay", "positionInput", "totalInQueue", "moveUpButton", "moveDownButton", "moveToPositionButton"]
  static values = {
    studentId: Number,
    position: Number,
    total: Number,
    moveUpUrl: String,
    moveDownUrl: String,
    moveToPositionUrl: String
  }

  connect() {
    this.updateButtonStates()
    this.updateButtons = this.updateButtonStates.bind(this)
    document.addEventListener('queue:positions:updated', this.updateButtons)
    this.isReordering = false

    this.setupAttempts = 0
    this.setupDataTablesIntegration()
  }

  disconnect() {
    document.removeEventListener('queue:positions:updated', this.updateButtons)
  }

  setupDataTablesIntegration() {
    this.setupAttempts++
    const table = this.getDataTable()

    if (table) {
      this.dataTableInstance = table

      table.off('search.dt.queuePosition')
      table.on('search.dt.queuePosition', () => {
        setTimeout(() => {
          this.handleTableUpdate()
        }, 150)
      })

      table.off('draw.dt.queuePosition')
      table.on('draw.dt.queuePosition', () => {
        if (!this.isReordering) {
          setTimeout(() => {
            this.updateAllButtonStates()
          }, 50)
        }
      })

      setTimeout(() => {
        this.reorderTableRows()
        this.updateAllButtonStates()
      }, 200)
    } else if (this.setupAttempts < 20) {
      setTimeout(() => {
        this.setupDataTablesIntegration()
      }, 200)
    }
  }

  getDataTable() {
    try {
      const tableElement = this.element.closest('table.datatable')
      if (!tableElement) return null

      if (typeof $ !== 'undefined' && $(tableElement).length) {
        const dt = $(tableElement).DataTable()
        return dt && dt.length ? dt : null
      }
      return null
    } catch (e) {
      console.warn('Error getting DataTable:', e)
      return null
    }
  }

  getQueuePositionController(element) {
    if (!element) return null

    if (element.hasAttribute('data-controller') && element.getAttribute('data-controller').includes('queue-position')) {
      return this.application.getControllerForElementAndIdentifier(element, 'queue-position')
    }

    const controllerElement = element.querySelector('[data-controller*="queue-position"]')
    if (!controllerElement) return null

    return this.application.getControllerForElementAndIdentifier(controllerElement, 'queue-position')
  }

  handleTableUpdate() {
    try {
      const dt = this.getDataTable()
      if (!dt) {
        this.reorderTableRows()
        this.updateAllButtonStates()
        return
      }

      const searchValue = dt.search()
      const isSearchActive = searchValue && searchValue.trim() !== ''

      if (isSearchActive) {
        this.reorderFilteredRows()
      } else {
        this.reorderTableRows()
      }

      this.updateAllButtonStates()
    } catch (e) {
      console.error('Error in handleTableUpdate:', e)
      this.reorderTableRows()
      this.updateAllButtonStates()
    }
  }

  async moveUp(event) {
    event.preventDefault()
    event.stopPropagation()

    this.performAction(this.moveUpUrlValue, "Moving up...")
  }

  async moveDown(event) {
    event.preventDefault()
    event.stopPropagation()

    this.performAction(this.moveDownUrlValue, "Moving down...")
  }

  async moveToPosition(event) {
    event.preventDefault()
    event.stopPropagation()

    const position = parseInt(this.positionInputTarget.value)

    if (!position || position < 1) {
      errorDialog("Validation Error", "Please enter a valid position number")
      return
    }

    const confirmed = await confirmDialog({ text: `Are you sure you want to move this student to position ${position} in the queue?` })
    if (!confirmed) {
      return
    }

    this.closeDropdown()
    const url = this.moveToPositionUrlValue.replace(':position', position)
    this.performAction(url, `Moving to position ${position}...`)
    this.positionInputTarget.value = ''
  }

  performAction(url, loadingMessage, data = {}) {
    this.setButtonsDisabled(true)

    let originalDisplayText = null
    if (this.hasPositionDisplayTarget) {
      originalDisplayText = this.positionDisplayTarget.textContent
      this.positionDisplayTarget.textContent = loadingMessage
    }

    const restoreDisplay = () => {
      if (this.hasPositionDisplayTarget && originalDisplayText) {
        this.positionDisplayTarget.textContent = originalDisplayText
      }
    }

    Rails.ajax({
      url: url,
      type: 'PATCH',
      dataType: 'json',
      data: data,
      success: (response) => {
        if (response.success) {
          this.updateDisplay(response)
          this.updateSwappedStudent(response)
          this.updateAffectedStudents(response)

          setTimeout(() => {
            this.handleTableUpdate()
          }, 200)

          successDialog("Success", response.message || "Queue position updated successfully")
        } else {
          restoreDisplay()
          errorDialog("Error", response.message || "Failed to update queue position")
        }
      },
      error: (xhr) => {
        console.error("Error updating queue position:", xhr)
        restoreDisplay()
        let errorMessage = "An error occurred while updating the queue position"

        if (xhr.message) errorMessage = xhr.message
        if (xhr.responseJSON && xhr.responseJSON.message) {
          errorMessage = xhr.responseJSON.message
        }

        errorDialog("Error", errorMessage)
      },
      complete: () => {
        this.setButtonsDisabled(false)
        this.updateButtonStates()
      }
    })
  }

  updateDisplay(data) {
    if (this.hasPositionDisplayTarget && data.queue_position_display) {
      this.positionDisplayTarget.textContent = `#${data.queue_position_display}`
    }

    if (this.hasTotalInQueueTarget && data.total_in_queue) {
      this.totalInQueueTarget.textContent = data.total_in_queue
    }

    if (data.queue_position) {
      this.element.dataset.queuePosition = data.queue_position
      this.positionValue = data.queue_position
    }

    if (data.total_in_queue !== undefined) {
      this.totalValue = data.total_in_queue
    }

    this.updateButtonStates()
  }

  setButtonsDisabled(disabled) {
    if (this.hasMoveUpButtonTarget) {
      this.moveUpButtonTarget.disabled = disabled
    }
    if (this.hasMoveDownButtonTarget) {
      this.moveDownButtonTarget.disabled = disabled
    }
    if (this.hasMoveToPositionButtonTarget) {
      this.moveToPositionButtonTarget.disabled = disabled
    }
  }

  updateButtonStates() {
    if (!this.hasPositionDisplayTarget) return

    const match = this.positionDisplayTarget.textContent.match(/#(\d+)\s+of\s+(\d+)/)
    if (!match) return

    const position = parseInt(match[1])
    const total = parseInt(match[2])

    if (this.hasMoveUpButtonTarget) {
      this.moveUpButtonTarget.disabled = (position === 1)
    }

    if (this.hasMoveDownButtonTarget) {
      this.moveDownButtonTarget.disabled = (position === total && total > 0)
    }
  }

  updateAllButtonStates() {
    const table = this.element.closest('table')
    if (!table) return

    const rows = table.querySelectorAll('tr[id^="student-row-"]')
    rows.forEach(row => {
      const controller = this.getQueuePositionController(row)
      if (!controller || !controller.hasPositionDisplayTarget) return

      const match = controller.positionDisplayTarget.textContent.match(/#(\d+)\s+of\s+(\d+)/)
      if (!match) return

      const position = parseInt(match[1])
      const total = parseInt(match[2])

      if (controller.hasMoveUpButtonTarget) {
        controller.moveUpButtonTarget.disabled = (position === 1)
      }

      if (controller.hasMoveDownButtonTarget) {
        controller.moveDownButtonTarget.disabled = (position === total && total > 0)
      }
    })
  }

  closeDropdown() {
    const dropdownToggle = this.element.querySelector('[data-toggle="dropdown"]')
    if (!dropdownToggle) return

    if (typeof $ !== 'undefined' && $(dropdownToggle).dropdown) {
      $(dropdownToggle).dropdown('hide')
    } else {
      const dropdownMenu = this.element.querySelector('.dropdown-menu')
      if (dropdownMenu) {
        dropdownMenu.classList.remove('show')
        dropdownToggle.setAttribute('aria-expanded', 'false')
      }
    }
  }

  updateSwappedStudent(data) {
    if (data.swapped_student) {
      this.updateStudentPosition(data.swapped_student)
    }
  }

  updateAffectedStudents(data) {
    if (data.affected_students && Array.isArray(data.affected_students)) {
      data.affected_students.forEach(student => {
        this.updateStudentPosition(student)
      })
    }
  }

  updateStudentPosition(studentData) {
    const studentRow = document.querySelector(`#student-row-${studentData.id}`)
    if (!studentRow) return

    const controller = this.getQueuePositionController(studentRow)
    if (!controller) return

    if (controller.hasPositionDisplayTarget && studentData.queue_position_display) {
      controller.positionDisplayTarget.textContent = `#${studentData.queue_position_display}`
    }

    if (studentData.queue_position !== undefined) {
      controller.element.dataset.queuePosition = studentData.queue_position
      controller.positionValue = studentData.queue_position
    }

    if (studentData.total_in_queue !== undefined) {
      if (controller.hasTotalInQueueTarget) {
        controller.totalInQueueTarget.textContent = studentData.total_in_queue
      }
      controller.totalValue = studentData.total_in_queue
    }

    document.dispatchEvent(new CustomEvent('queue:positions:updated'))
  }

  reorderTableRows() {
    if (this.isReordering) return
    this.isReordering = true

    try {
      const dt = this.getDataTable()
      const table = this.element.closest('table')
      if (!table) return

      const tbody = table.querySelector('tbody')
      if (!tbody) return

      const allRows = Array.from(tbody.querySelectorAll('tr[id^="student-row-"]'))

      if (allRows.length === 0) return

      const rowsWithPositions = allRows.map(row => {
        const controller = this.getQueuePositionController(row)
        let position = null

        if (controller) {
          if (controller.element.dataset.queuePosition) {
            position = parseInt(controller.element.dataset.queuePosition)
          } else if (controller.hasPositionDisplayTarget) {
            const text = controller.positionDisplayTarget.textContent.trim()
            const match = text.match(/#(\d+)\s+of\s+\d+/)
            if (match) {
              position = parseInt(match[1])
            }
          }
        }

        return { row, position: position || 999999 }
      })

      rowsWithPositions.sort((a, b) => {
        if (a.position === b.position) return 0
        return a.position - b.position
      })

      rowsWithPositions.forEach(({ row }) => {
        tbody.appendChild(row)
      })

      if (dt) {
        setTimeout(() => {
          try {
            dt.draw(false)
          } catch (e) {
            console.warn('Error redrawing DataTable:', e)
          } finally {
            setTimeout(() => { this.isReordering = false }, 50)
          }
        }, 100)
      } else {
        this.isReordering = false
      }
    } catch (e) {
      this.isReordering = false
      console.error('Error in reorderTableRows:', e)
    }
  }

  reorderFilteredRows() {
    if (this.isReordering) return
    this.isReordering = true

    try {
      const dt = this.getDataTable()
      if (!dt) {
        this.isReordering = false
        this.reorderTableRows()
        return
      }

      const tableNode = dt.table().node()
      if (!tableNode) {
        this.isReordering = false
        this.reorderTableRows()
        return
      }

      const tbody = tableNode.querySelector('tbody')
      if (!tbody) {
        this.isReordering = false
        return
      }

      const visibleRows = []
      dt.rows({ search: 'applied' }).every(function() {
        const node = this.node()
        if (node && node.id && node.id.startsWith('student-row-')) {
          visibleRows.push(node)
        }
      })

      if (visibleRows.length === 0) {
        this.isReordering = false
        return
      }

      const rowsWithPositions = visibleRows.map(row => {
        const controller = this.getQueuePositionController(row)
        let position = null

        if (controller) {
          if (controller.element.dataset.queuePosition) {
            position = parseInt(controller.element.dataset.queuePosition)
          } else if (controller.hasPositionDisplayTarget) {
            const text = controller.positionDisplayTarget.textContent.trim()
            const match = text.match(/#(\d+)\s+of\s+\d+/)
            if (match) {
              position = parseInt(match[1])
            }
          }
        }

        return { row, position: position || 999999 }
      })

      rowsWithPositions.sort((a, b) => {
        if (a.position === b.position) return 0
        return a.position - b.position
      })

      rowsWithPositions.forEach(({ row }) => {
        tbody.appendChild(row)
      })

      setTimeout(() => {
        try {
          dt.draw(false)
        } catch (e) {
          console.warn('Error redrawing filtered DataTable:', e)
        } finally {
          setTimeout(() => { this.isReordering = false }, 50)
        }
      }, 100)
    } catch (e) {
      this.isReordering = false
      console.warn('Error reordering filtered rows:', e)
      this.reorderTableRows()
    }
  }
}
